import React from 'react';

/**
 * "Why ShiftWagon" — the uniform six-cell benefits grid from Redesign v2.
 *
 * Every cell is identical in weight: same padding, same icon tile, no accent
 * borders and no per-card elevation. The only rule between them is the 1px grid
 * gap showing the container behind, so the six read as one object.
 *
 * Icons are inline 24px stroke glyphs (verbatim from the design) rather than an
 * icon dependency — six paths do not justify a package.
 */

interface Benefit {
  title: string;
  copy: string;
  /** Path data drawn inside a 0 0 24 24 viewBox. */
  icon: React.ReactNode;
}

const BENEFITS: Benefit[] = [
  {
    title: 'Estimate upfront',
    copy: 'Route and vehicle pricing is shown before you confirm.',
    icon: (
      <>
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </>
    ),
  },
  {
    title: 'Live tracking',
    copy: 'Follow the driver from match to drop-off, in real time on the map.',
    icon: (
      <>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </>
    ),
  },
  {
    title: 'Verified drivers',
    copy: 'Every driver completes onboarding before they can receive work.',
    icon: (
      <>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
      </>
    ),
  },
  {
    title: 'Now or scheduled',
    copy: 'Dispatch immediately, or reserve a truck for a later date and time.',
    icon: (
      <>
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </>
    ),
  },
  {
    title: 'In-trip messaging',
    copy: 'Keep every update in the booking thread — nothing lost on WhatsApp.',
    icon: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  },
  {
    title: 'Card payments',
    copy: 'Secure card authorization happens before the trip moves ahead.',
    icon: (
      <>
        <rect x="1" y="4" width="22" height="16" rx="2" />
        <line x1="1" y1="10" x2="23" y2="10" />
      </>
    ),
  },
];

const BenefitsSection: React.FC = () => {
  return (
    <section className="sw-section">
      <div className="container">
        <div className="sw-section-head">
          <span className="sw-eyebrow">Why ShiftWagon</span>
          <h2>Built to take the guesswork out of moving.</h2>
        </div>

        <div className="sw-benefit-grid">
          {BENEFITS.map((benefit) => (
            <div className="sw-benefit" key={benefit.title}>
              <span className="sw-benefit-icon">
                {/* Decorative: each glyph restates its adjacent heading. */}
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  focusable="false"
                >
                  {benefit.icon}
                </svg>
              </span>
              <h3 className="sw-benefit-title">{benefit.title}</h3>
              <p className="sw-benefit-copy">{benefit.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
